import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  ClockIcon,
  FolderIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  UsersIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/use-auth';
import { signOutUser } from '@/services/auth';
import { UserMenu } from './UserMenu';

const primaryNav = [
  { name: 'Dashboard', to: '/', icon: HomeIcon },
  { name: 'Time Tracking', to: '/time-tracking', icon: ClockIcon },
  { name: 'Projects', to: '/projects', icon: FolderIcon },
] as const;

const secondaryNav = [
  { name: 'Approvals', to: '/approvals', icon: ClipboardDocumentCheckIcon },
  { name: 'Reports', to: '/reports', icon: ChartBarIcon },
  { name: 'Users', to: '/users', icon: UsersIcon },
  { name: 'Invoices', to: '/invoices', icon: DocumentTextIcon },
  { name: 'Settings', to: '/settings', icon: Cog6ToothIcon },
] as const;

function desktopLinkClasses({ isActive }: { isActive: boolean }): string {
  const base =
    'relative px-3 py-2 text-sm font-medium transition-colors rounded-lg';
  return isActive
    ? `${base} text-primary-700 bg-primary-50 dark:text-primary-300 dark:bg-primary-950`
    : `${base} text-surface-600 hover:text-surface-900 hover:bg-surface-100 dark:text-surface-400 dark:hover:text-surface-200 dark:hover:bg-surface-800`;
}

function mobileLinkClasses({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors';
  return isActive
    ? `${base} bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300`
    : `${base} text-surface-600 hover:bg-surface-50 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-200`;
}

export function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-50 dark:bg-surface-950">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-lg focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>
      {/* Top navigation bar */}
      <header className="relative z-30 border-b border-surface-200 bg-white dark:border-surface-800 dark:bg-surface-900">
        <div className="mx-auto flex h-16 max-w-[90rem] items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Left: Logo + primary nav */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <NavLink
              to="/"
              className="flex items-center gap-2 text-xl font-bold tracking-tight text-primary-700 dark:text-primary-400"
            >
              <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="16" cy="16" r="15" fill="#0d9488" />
                <line x1="16" y1="2.5" x2="16" y2="5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
                <line x1="29.5" y1="16" x2="26.5" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
                <line x1="16" y1="29.5" x2="16" y2="26.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
                <line x1="2.5" y1="16" x2="5.5" y2="16" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.45" />
                <path d="M11 6.5L16 17L21 6.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="16" cy="17" r="2" fill="white" />
              </svg>
              Voltasis
            </NavLink>

            {/* Desktop primary nav */}
            <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
              {primaryNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={desktopLinkClasses}
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Right: User menu (desktop) + hamburger (mobile) */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <UserMenu userName={user?.name} userEmail={user?.email} />
            </div>
            <button
              type="button"
              className="rounded-lg p-2 text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-700 md:hidden dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-200"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 top-16 z-20 bg-black/30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <div
              ref={menuRef}
              id="mobile-menu"
              className="absolute left-0 right-0 top-full z-30 border-b border-surface-200 bg-white shadow-lg md:hidden dark:border-surface-800 dark:bg-surface-900"
            >
              <nav className="mx-auto max-w-[90rem] px-4 py-3 sm:px-6" aria-label="Mobile">
                <div className="space-y-0.5">
                  {primaryNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/'}
                      className={mobileLinkClasses}
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>

                <div className="my-2 border-t border-surface-100 dark:border-surface-800" />

                <div className="space-y-0.5">
                  {secondaryNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={mobileLinkClasses}
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>

                <div className="my-2 border-t border-surface-100 dark:border-surface-800" />

                {/* User info + sign out */}
                <div className="flex items-center justify-between px-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                      {user?.name?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-surface-900 dark:text-surface-100">
                        {user?.name}
                      </p>
                      <p className="truncate text-xs text-surface-500 dark:text-surface-400">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => signOutUser()}
                    className="rounded-lg p-2 text-surface-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400"
                    aria-label="Sign out"
                  >
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              </nav>
            </div>
          </>
        )}
      </header>

      {/* Page content */}
      <main id="main-content" className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[90rem] p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
